import { useQuery } from '@tanstack/react-query';
import { SelectedProvider, SelectedProviderType } from '@uplift/pages';
import { CalendarDateService } from '@uplift/services';
import { ProviderMedicalRole, SeeClientsEnum } from '@uplift/types';
import { PROFILE_ROW_QUERY_KEYS } from '../profile.utils';
import { getTherapeuticStyles, InsuranceCompanyPayerType } from '@uplift/api';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { IconWithText } from '@uplift/ui-v2';
import { ArrowDownward } from '@mui/icons-material';

export const IdealPreview: React.VFC = () => {
  const { classes } = useIdealPreviewStyles();
  const idealProviderMock = useIdealProviderMock();

  return (
    <Box className={classes.root}>
      <Box className={classes.tipContainer}>
        <IconWithText
          className={classes.tip}
          icon={<ArrowDownward fontSize="small" className={classes.tipIcon} />}
        >
          Scroll down to see an example of a great profile
        </IconWithText>
      </Box>
      <SelectedProvider
        stateId={idealProviderMock.userProfile.practice.locations[0].state.id}
        selectedProvider={idealProviderMock}
        onScheduleSession={() => {
          return;
        }}
        isPublicProfile
        isPreview
        isScrollableView
        hideScheduler
        hideAcceptedInsurances
        mainContainerWidth="588px"
      />
    </Box>
  );
};

const useIdealPreviewStyles = makeStyles({ name: 'IdealPreview' })(theme => ({
  root: {
    borderLeft: `1px solid ${theme.palette.divider}`,
  },
  tipContainer: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.info.background,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'center',
  },
  tipIcon: {
    color: theme.palette.info.main,
  },
  tip: {
    fontWeight: 450,
  }
}));


const useIdealProviderMock = () => {
  /**
    * Fetches all therapeutic styles for preview.
    * This approach avoids hardcoding all labels and descriptions,
    * as they are already prefetched for FUE anyway.
    */
  const { data: therapeuticStyles } = useQuery({
    queryKey: PROFILE_ROW_QUERY_KEYS.therapeuticStyle,
    queryFn: async () => {
      const result = await getTherapeuticStyles();

      return result.data;
    },
    initialData: [],
  });

  const createListItems = (names: string[]) =>
    names.map((name, index) => ({
      id: index.toString(),
      name,
    }));

  const idealProviderMock: SelectedProviderType = {
    id: '1',
    createdAt: CalendarDateService.date().subtract(10, 'month').toISOString(),
    firstName: 'Krista',
    lastName: 'Gordon',
    nameDisplay: {
      first: 'Krista',
      last: 'Gordon',
      full: 'Krista Gordon',
      defaultDisplay: 'Krista Gordon'
    },
    medicalRole: ProviderMedicalRole.therapist,
    userProfile: {
      mentalRoles: [],
      preferredName: 'Krista',
      pronouns: 'She/Her',
      shortBio: 'Hello! I’m Krista Gordon, a Licensed Clinical Social Worker in DC and Virginia. Whatever changes you’re going through in life, I’m here to support you. I take a collaborative and compassionate approach to learn about you, process your experiences, and help you identify your strengths. \n\nI’m here to provide a space where you can sort through your thoughts and feelings freely and without any judgement. Together, we can work towards your goals and toward a place where you feel you can move forward.',
      license: [
        {
          id: '1',
          name: 'Licensed Clinical Social Worker (LCSW)',
          state: {
            id: '1',
            shortName: 'DC',
            fullName: 'District of Columbia'
          }
        },
        {
          id: '2',
          name: 'Licensed Clinical Social Worker (LCSW)',
          state: {
            id: '1',
            shortName: 'VA',
            fullName: 'Virginia'
          }
        }
      ],
      practice: {
        practiceName: '',
        practiceServiceType: SeeClientsEnum.telehealth,
        locations: [
          {
            id: '1',
            addressLine1: '123 Main St',
            addressLine2: '',
            city: 'New York',
            state: {
              id: '1',
              shortName: 'NY',
              fullName: 'New York'
            },
            zip: '10018',
            hideStreetAddress: true,
          },
          {
            id: '2',
            addressLine1: '456 Main St',
            addressLine2: '',
            city: 'Dunkirk',
            state: {
              id: '1',
              shortName: 'NY',
              fullName: 'New York'
            },
            zip: '14048',
            hideStreetAddress: true,
          }
        ]
      },
      avatarAsset: {
        url: 'https://scdn.joinuplift.co/public/avatars/ideal-profile-pic.png',
      },
    },
    supportedAgeGroups: createListItems([
      'Children (<12)',
      'Teens (13–17)',
      'Young Adults (18–25)',
      'Adults (26–64)',
      'Seniors (65+)',
    ]),
    modalities: createListItems([
      'Individuals',
      'Couples',
      'Family',
    ]),
    specialties: createListItems([
      'Anxiety',
      'Depression',
      'Family Relationships',
      'Trauma',
      'Grief, Loss',
      'Romantic relationships',
      'Career issues',
      'Post-Traumatic Stress Disorder',
      'Anger Management',
      'ADD/ADHD',
    ]),
    treatmentApproach: createListItems([
      'Cognitive Behavioral Therapy (CBT)',
      'Person-Centered Therapy',
      'Solution Focused Brief Therapy (SFBT)',
      'Motivational Interviewing (MI)',
      'Mindfulness-Based Cognitive Therapy (MBCT)',
      'Strength-Based Therapy',
      'Trauma-Focused Therapy',
      'Dialectical Behavioral Therapy (DBT)',
    ]),
    ethnicity: createListItems([
      'South Asian',
    ]),
    faith: createListItems([
      'Secular/Non-religious',
    ]),
    communityExpertise: {
      ethnicity: createListItems([
        'Asian',
        'Black or African Descent',
        'Middle Eastern or North African',
        'South Asian',
      ]),
      faith: createListItems([
        'Buddhist',
        'Christian',
        'Muslim',
        'Secular/Non-religious',
      ]),
      general: createListItems([
        'Cancer and Treatment',
        'Death and dying',
      ]),
    },
    genders: createListItems([
      'Cisgender woman',
    ]),
    languages: createListItems([
      'Arabic',
      'English',
      'Hindi',
      'Urdu',
    ]),
    therapeuticStyles: therapeuticStyles,
    qualification: {
      degrees: [
        {
          id: '1',
          degree: "Bachelor's",
          fieldOfStudy: 'Psychology',
          school: 'Yale University',
          yearGraduated: 2000,
        },
        {
          id: '2',
          degree: "Master's",
          fieldOfStudy: 'Psychology',
          school: 'Yale University',
          yearGraduated: 2000,
        },
      ],
      yearsInPractice: 10,
    },
    npi: '0123456789',
    supportedInsuranceCompanies: [
      {
        id: '1',
        name: 'CareFirst BlueCross Blue Shield',
        logoUrl: 'https://scdn.joinuplift.co/public/insurance_assets/CareFirst.svg',
        url: 'https://individual.carefirst.com/individuals-families/home.page',
        createdAt: '2021-05-17T16:33:49.481867+00:00',
        updatedAt: '2022-04-20T08:48:12.906872+00:00',
        order: 1,
        shortName: 'CareFirst',
        key: 'carefirst',
        payorId: 'SB580',
        available: true,
        metaGrouping: null,
        delegated: true,
        payerType: InsuranceCompanyPayerType.delegated,
      }
    ]
  };

  return idealProviderMock;
};
